import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useSnapActions,
  useSnapAnchorElement,
  useSnapInVisitNote,
  useSnapMode,
  useSnapVisitDetails,
} from 'views/EMR/stores/snap';
import Button from 'pages/Dashboard/components/Button';
import Popper from '@mui/material/Popper';
import IconButton from '@mui/material/IconButton';
import MicIcon from 'core/icons/mic';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import useRecordingVisualizer from 'views/EMR/components/Snap/visualizer/useRecordingVisualizer';
import ToolTip from 'core/components/Tooltip';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import AudioPlayerWithVisualizer from 'views/EMR/components/Snap/visualizer/AudioPlayerWithVisualizer';
import {
  useNavigate,
} from 'react-router-dom';
import NotesIcon from 'core/icons/notes';
import PatientName from 'views/EMR/components/PatientName';
import {
  formatDateTime,
} from 'utils/date';
import defer from 'lodash/defer';
import Spinner from 'pages/Dashboard/components/Spinner';
import Estimation from 'views/EMR/components/Snap/Estimation';
import {
  useConfirmDialog,
} from 'core/hooks';
import {
 datadogLogs,
} from '@datadog/browser-logs';

const CANCEL_MESSAGE_GENERATING = 'Note generation is in progress. Cancelling will stop the process and cannot be undone.';
const CANCEL_MESSAGE_RECORDED = 'Your recording is ready to generate a note. Cancelling will discard it and cannot be undone.';
const CANCEL_MESSAGE_RECORDING = 'Recording is in progress. Cancelling will discard it and cannot be undone.';

type ConfirmWarningContentProps = {
  message: string;
};

function ConfirmWarningContent({ message }: ConfirmWarningContentProps) {
  return (
    <div className="flex flex-col gap-2 mb-2">
      <div className="flex items-center gap-2 rounded-md bg-red-100 px-3 py-2 text-xs text-gray-700 font-medium">
        <WarningRoundedIcon className="!w-4 !h-4 shrink-0 !text-red-700" />
        <span>{message}</span>
      </div>
    </div>
  );
}

function getCancelMessage(
  isGeneratingNote: boolean,
  recordedBlob: Blob | null,
  recordingState: string,
): string | null {
  if (isGeneratingNote) {
    return CANCEL_MESSAGE_GENERATING;
  }
  if (!isNil(recordedBlob)) {
    return CANCEL_MESSAGE_RECORDED;
  }
  if (recordingState !== 'idle') {
    return CANCEL_MESSAGE_RECORDING;
  }
  return null;
}

type HeaderProps = {
  theme: 'light' | 'dark';
};

function Header({
  theme,
}: HeaderProps) {
  const visitDetails = useSnapVisitDetails();
  const snapMode = useSnapMode();
  const inVisitNote = useSnapInVisitNote();

  const navigate = useNavigate();

  const isPopoverOnly = snapMode === 'popoverOnly';

  return isPopoverOnly ? null : (
    <div className={clsx('text-sm text-gray-700 flex gap-2 items-start justify-between border-b pb-2 border-gray-200', {
      '!text-gray-300 !border-gray-600': theme === 'dark',
    })}
    >
      <div className="flex flex-1 truncate flex-col">
        <PatientName
          patient={{
            patientId: visitDetails?.patientId,
            // todo: check if we can do better than this
            formattedNames: {
              fullName: visitDetails?.patientName,
            },
          }}
          className={clsx('text-gray-800 !text-sm', {
            '!text-gray-200': theme === 'dark',
          })}
          onNavigateAway={() => {}}
        />
        <span className={clsx('text-gray-700 text-xs font-semibold', {
          '!text-gray-50': theme === 'dark',
        })}
        >
          Visited on
          {' '}
          {formatDateTime(visitDetails?.visitDateTime ?? '')}
        </span>
      </div>
      <ToolTip title={inVisitNote ? 'Already in the visit note' : 'Go to visit note'}>
        <span className={clsx({
          '!cursor-not-allowed': inVisitNote,
        })}
        >
          <Button
            variant="outlined"
            className={clsx('!w-max border rounded  font-semibold !text-xs !h-6 py-0.5 px-1 flex items-center text-blue-500 gap-2', {
              '!border-blue-300 !text-blue-300': theme === 'dark',
              '!cursor-not-allowed !border-blue-300 !text-blue-300': inVisitNote,
              '!border-blue-500 !text-blue-500': !inVisitNote,
            })}
            onClick={() => {
              navigate(`/encounters/${visitDetails?.encounterId ?? 0}`);
            }}
            disabled={inVisitNote}
          >
            <NotesIcon className="!h-4 !w-4 !text-inherit" />
            Visit Note
          </Button>
        </span>
      </ToolTip>

    </div>
  );
}

type Props = {
  recordingState: string;
  startRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  endRecording: () => void;
  recordedBlob: Blob | null;
  onSubmit: () => void;
  stream: MediaStream | null;
  onDelete: () => void;
  isGeneratingNote: boolean;
  duration: number;
  currentState: string;
  showSilentDetection?: boolean;
  error?: string | null;
};

export const formatDurationToTime = (seconds: number) => {
  const isInvalid = Number.isNaN(seconds) || !Number.isFinite(seconds) || seconds <= 0;
  let formattedTime = '00:00';
  if (!isInvalid) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    formattedTime = `${mins < 10 ? `0${mins}` : mins}:${secs < 10 ? `0${secs}` : secs}`;
  }
  return formattedTime;
};

export default function SnapPopover({
  currentState,
  recordingState,
  startRecording,
  pauseRecording,
  resumeRecording,
  endRecording,
  recordedBlob,
  onSubmit,
  stream,
  onDelete,
  isGeneratingNote,
  duration,
  showSilentDetection = false,
  error,
}: Props) {
  // Our hook provides the visualization functions and the canvas ref
  const {
    start: startVisualizer,
    stop: stopVisualizer,
    pause: pauseVisualizer,
    resume: resumeVisualizer,
    containerRef,
    initializeCanvas,
  } = useRecordingVisualizer();

  useEffect(() => {
    if (isNil(recordedBlob) && recordingState !== 'idle') {
      initializeCanvas();
    }
  }, [recordedBlob, initializeCanvas, recordingState]);

  const isPausedRef = useRef(false);

  // This useEffect is the "glue". It watches for the stream from your hook.
  // When the stream appears, it starts the visualizer.
  useEffect(() => {
    defer(() => {
      const recordingStarted = !isNil(stream) && recordingState === 'recording';
      const isPaused = isPausedRef.current;
      if (recordingStarted && isPaused) {
        resumeVisualizer();
        isPausedRef.current = false;
      } else if (recordingStarted && !isPaused) {
        startVisualizer(stream);
      }
    });
  }, [stream, recordingState, startVisualizer]);

  useEffect(() => () => stopVisualizer(), [stopVisualizer]);

  const pause = () => {
    pauseRecording();
    pauseVisualizer();
    isPausedRef.current = true;
  };

  const snapMode = useSnapMode();
  const anchorElement = useSnapAnchorElement();
  const { closeSnap } = useSnapActions();
  const { confirm, closeConfirm } = useConfirmDialog();
  const theme = snapMode === 'withMinimizedControls' ? 'dark' : 'light';
  const isPopoverOnly = snapMode === 'popoverOnly';

  const onCancel = () => {
    datadogLogs.logger.info(`[Snap] Cancelled by user - ${formatDurationToTime(duration ?? 0)}`);
    closeSnap();
  };

  const disabled = isNil(recordedBlob) || isGeneratingNote;

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isRecordingPlaying, setIsRecordingPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!isNil(recordedBlob)) {
      setCurrentTime(0);
      setIsRecordingPlaying(false);
    }
  }, [recordedBlob]);

  const handlePlayPause = async () => {
    if (!isNil(audioRef?.current)) {
      try {
        if (isRecordingPlaying) {
          audioRef.current.pause();
        } else {
          await audioRef.current.play();
        }
      } catch (error) {
        console.error('[Snap] Audio playback error:', error);
      }
    }
  };

  return (
    <Popper
      placement={isPopoverOnly ? 'top-start' : 'bottom-start'}
      anchorEl={anchorElement}
      className="z-50"
      open={!isNil(anchorElement)}
    >
      <div className={clsx('mb-1 bg-white rounded-md shadow-lg p-4 w-[380px] flex flex-col gap-2 border border-gray-200', {
        '!bg-gray-700 !shadow-gray-900/50': theme === 'dark',
        fixed: isPopoverOnly,
      })}
      >
        <Header theme={theme} />
        <div className="w-full h-[56px]">
          {isNil(recordedBlob) && recordingState !== 'idle' && (
            <canvas
              ref={containerRef}
              width="346"
              height="56"
              style={{ borderRadius: '4px' }}
            />
          )}
          {isNil(recordedBlob) && recordingState === 'idle' && (
            <div className={clsx('flex items-center justify-center h-full rounded border', {
              'bg-gray-600 border-gray-600 text-gray-300': theme === 'dark',
              'bg-gray-50 border-gray-100 text-gray-600': theme === 'light',
            })}
            >
              <span className="font-medium text-xs text-center px-4">
                Start Dictation to record up to 30 minutes and generate a new visit note
              </span>
            </div>
          )}
          {!isNil(recordedBlob) && (
            <AudioPlayerWithVisualizer
              recordedBlob={recordedBlob}
              audioRef={audioRef}
              onPlayingChange={setIsRecordingPlaying}
              onTimeUpdate={(time) => {
                setCurrentTime(time);
              }}
            />
          )}
        </div>
        {recordingState === 'recording' && (
          <div
            className={clsx(
              'text-slate-600 bg-gray-50 rounded-md text-xs flex gap-2 items-center font-semibold overflow-hidden',
              'transition-all duration-200 ease-out',
              {
                'opacity-100 h-8 p-2': showSilentDetection,
                'opacity-0 h-0 pointer-events-none -my-2': !showSilentDetection,
              },
            )}
          >
            <span className="font-semibold">
              No speech detected. Speaking? Check mic
            </span>
          </div>
        )}
        {!isNil(error) && (
          <div
            className={clsx(
              ' bg-gray-50 rounded-md text-xs flex gap-2 items-center font-semibold overflow-hidden',
              'transition-all duration-200 ease-out',
              'bg-red-100 text-slate-600 text-center w-full',
              {
                'opacity-100 h-8 p-2': !isNil(error),
                'opacity-0 h-0 pointer-events-none -my-2': isNil(error),
              },
            )}
          >
            <NotInterestedOutlinedIcon className="!w-4 !h-4 !text-red-500" />
            {error}
          </div>
        )}
        <div className="flex gap-2 items-center justify-between h-9">
          {recordingState === 'idle' && (
            <Button
              variant="contained"
              onClick={startRecording}
              color="primary"
              className={clsx('flex items-center gap-2 pl-1.5 pr-2 !border-0 !min-w-full', {
                ' hover:!bg-blue-400': theme === 'dark',
              })}
            >
              <MicIcon className="!w-5 !h-5" />
              Start Dictation
            </Button>
          )}
          {/* recording stopped - timer */}
          {!isNil(recordedBlob) && (
            <div className="flex items-center">
              <ToolTip title={isRecordingPlaying ? 'Pause' : 'Play'}>
                <span>
                  <IconButton
                    onClick={handlePlayPause}
                    disabled={isGeneratingNote}
                    className={clsx('!w-8 !h-8 ', {
                      '!text-gray-300': theme === 'dark',
                      '!text-gray-700': theme === 'light',
                      '!cursor-not-allowed !pointer-events-auto': isGeneratingNote,
                      'hover:!bg-gray-600': theme === 'dark' && !isGeneratingNote,
                    })}
                  >
                    {isRecordingPlaying
                      ? <PauseRoundedIcon className="!w-8 !h-8" />
                      : <PlayArrowRoundedIcon className="!w-8 !h-8" />}
                  </IconButton>
                </span>
              </ToolTip>
              <div className="flex items-center gap-1">
                <span className={clsx('font-medium text-base', {
                  'text-gray-700': theme === 'light',
                  'text-gray-300': theme === 'dark',
                })}
                >
                  {formatDurationToTime(currentTime)}
                  /
                  {formatDurationToTime(duration)}
                </span>
              </div>
            </div>
          )}
          {/* recording in progress - timer & state */}
          {isNil(recordedBlob) && recordingState !== 'idle' && (
            <>
              <div className="flex items-center gap-0.5">
                <ToolTip title={recordingState === 'recording' ? 'Pause Recording' : 'Resume Recording'}>
                  <span>
                    <IconButton
                      className={clsx('!p-1 rounded-full', {
                        '!text-gray-300 hover:!bg-gray-600': theme === 'dark',
                      })}
                      onClick={recordingState === 'recording' ? pause : resumeRecording}
                    >
                      {recordingState === 'recording'
                        ? <PauseRoundedIcon className="!w-6 !h-6" />
                        : <PlayArrowRoundedIcon className="!w-6 !h-6" />}
                    </IconButton>
                  </span>
                </ToolTip>
                <span className={clsx('font-semibold text-xl block w-[60px]', {
                  'text-white': theme === 'dark',
                  'text-gray-800': theme === 'light',
                })}
                >
                  {formatDurationToTime(duration)}
                </span>
                <span className={clsx('font-medium text-xs w-16', {
                  'text-gray-400': theme === 'light',
                  'text-gray-300': theme === 'dark',
                })}
                >
                  {recordingState === 'recording' ? 'Recording' : 'Paused'}
                </span>
              </div>
              <div>
                <ToolTip title="End Dictation">
                  <span>
                    <IconButton
                      className={clsx('!p-1', {
                        '!text-gray-300 hover:!bg-gray-600': theme === 'dark',
                      })}
                      onClick={endRecording}
                    >
                      <StopRoundedIcon className="!w-6 !h-6   !text-red-500" />
                    </IconButton>
                  </span>
                </ToolTip>
              </div>
            </>
          )}
        </div>
        {!isNil(recordedBlob) && (
          <Estimation theme={theme} />
        )}
        <div className="flex flex-col gap-2">
          {(!disabled || isGeneratingNote) && (
            <ToolTip
              title="Audio recording is required"
              hide={!isNil(recordedBlob)}
            >
              <div className={clsx('w-full h-8', { '!cursor-not-allowed': disabled })}>
                <Button
                  disabled={disabled}
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={onSubmit}
                  className={clsx('!min-w-[unset] !max-w-[unset] !w-full !h-full', {
                    '!text-white': theme === 'dark' && !disabled,
                    '!text-white hover:!bg-gray-400 !bg-gray-400': theme === 'dark' && disabled,
                  })}
                >
                  {isGeneratingNote
                    ? (
                      <div className="flex items-center gap-1">
                        <Spinner />
                        <span>{currentState}</span>
                      </div>
                    )
                    : 'Generate Note'}
                </Button>
              </div>
            </ToolTip>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              const message = getCancelMessage(isGeneratingNote, recordedBlob, recordingState);
              if (!isNil(message)) {
                confirm({
                  title: 'Cancel Recording',
                  content: <ConfirmWarningContent message={message} />,
                  confirmText: 'Yes, Cancel',
                  cancelText: 'Keep Recording',
                  onConfirm: () => {
                    onCancel();
                    closeConfirm();
                  },
                  className: '!space-y-0',
                  classes: {
                    footer: '!pt-4',
                  },
                });
              } else {
                onCancel();
              }
            }}
            className={clsx('!w-full !min-w-[unset] !max-w-[unset] !h-8', {
              '!text-white hover:!bg-gray-600': theme === 'dark',
            })}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Popper>
  );
}
